import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { SimpleDropdown } from './ui/simple-dropdown';
import { X, Save, User, Briefcase } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { SchoolUserEditRequest } from '../api/types';
import { educationAPI} from '../api/axiosClient';

interface SchoolUserEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  schoolUserId: string | null;
  onSuccess: () => void;
}

const SchoolUserEditForm: React.FC<SchoolUserEditFormProps> = ({
  isOpen,
  onClose,
  schoolUserId,
  onSuccess
}) => {
  const handleClose = useCallback(() => {
    lastLoadedSchoolUserId.current = null;
    onClose();
  }, [onClose]);
  const [formData, setFormData] = useState<SchoolUserEditRequest>({
    id: '',
    firstName: '',
    lastName: '',
    title: '',
    userStatus: 1,
    roles: 1
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const lastLoadedSchoolUserId = useRef<string | null>(null);

  const normalizeRole = (roles: any): 2 | 5 => {
  if (roles === 2 || roles === 5) return roles;

  if (typeof roles === "string") {
    const v = roles.toLowerCase();
    if (v.includes("manager")) return 2;
    if (v.includes("schooluser") || v.includes("user")) return 5;
  }

  if (Array.isArray(roles) && roles.length > 0) {
    return normalizeRole(roles[0]);
  }  return 5;
};


  // Load school user data when schoolUserId changes
  useEffect(() => {
    const loadSchoolUserData = async () => {
      if (!schoolUserId) {
        return;
      }
      
      // Aynı ID için zaten yükleme yapıldıysa tekrar yapma
      if (lastLoadedSchoolUserId.current === schoolUserId) {
        return;
      }

      lastLoadedSchoolUserId.current = schoolUserId;
      setIsLoading(true);
      try {
        const response = await educationAPI.getSchoolUserDetails(schoolUserId);
        
        if (response.success && response.data) {
          const data = response.data;
          const user = data.user || {};
          
        setFormData({
          id: data.id || schoolUserId || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          title: data.title || '',
          userStatus: user.userStatus || 1,
          roles: normalizeRole(data.roles ?? user.roles) as number, // ✅
        });

        } else {
          toast.error('Okul kullanıcısı bilgileri yüklenirken hata oluştu');
          handleClose();
        }
      } catch (error) {
        console.error('Error loading school user data:', error);
        toast.error('Okul kullanıcısı bilgileri yüklenirken hata oluştu');
        handleClose();
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && schoolUserId) {
      loadSchoolUserData();
    }
  }, [isOpen, schoolUserId, handleClose]);

  const handleInputChange = (field: keyof SchoolUserEditRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Ad zorunludur';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Soyad zorunludur';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Unvan zorunludur';
    }

    if (![2, 5].includes(formData.roles)) {
      newErrors.roles = 'Rol seçiniz';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await educationAPI.updateSchoolUser(formData.id, {
        id: formData.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title,
        userStatus: formData.userStatus,
        roles: formData.roles,
      });
      
      if (response.success) {
        toast.success('Okul kullanıcısı başarıyla güncellendi');
        onSuccess();
        handleClose();
      } else {
        toast.error(response.message || 'Okul kullanıcısı güncellenirken hata oluştu');
      }
    } catch (error) {
      console.error('Error updating school user:', error);
      toast.error('Okul kullanıcısı güncellenirken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Okul Kullanıcısını Düzenle</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                <p className="text-gray-500">Okul kullanıcısı bilgileri yükleniyor...</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ad */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span>Ad <span className="text-red-500">*</span></span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400 ${
                      errors.firstName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Adını girin"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                  )}
                </div>

                {/* Soyad */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span>Soyad <span className="text-red-500">*</span></span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400 ${
                      errors.lastName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Soyadını girin"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                  )}
                </div>
                {/* Rol */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span>Rol <span className="text-red-500">*</span></span>
                  </label>

                  <SimpleDropdown
                    options={[
                      { id: '2', label: 'Okul Yöneticisi', value: 2 },
                      { id: '5', label: 'Okul Kullanıcısı', value: 5 },
                    ]}
                    value={
                      [
                        { id: '2', label: 'Okul Yöneticisi', value: 2 },
                        { id: '5', label: 'Okul Kullanıcısı', value: 5 },
                      ].find(r => r.value === formData.roles) || { id: '5', label: 'Okul Kullanıcısı', value: 5 }
                    }
                    onChange={(role) => {
                      if (role) handleInputChange('roles', role.value);
                    }}
                    placeholder="Rol seç..."
                    className="w-full"
                  />


                  {errors.roles && (
                    <p className="text-red-500 text-xs mt-1">{errors.roles}</p>
                  )}
                </div>

                {/* Unvan */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Briefcase className="w-4 h-4 text-gray-500" />
                    <span>Unvan <span className="text-red-500">*</span></span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400 ${
                      errors.title ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Örn: Öğretmen, Müdür Yardımcısı"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-xs mt-1">{errors.title}</p>
                  )}
                </div>

                {/* Durum */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span>Durum</span>
                  </label>
                  <SimpleDropdown
                    options={[
                      { id: '1', label: 'Aktif', value: 1 },
                      { id: '2', label: 'Pasif', value: 2 },
                      { id: '3', label: 'Doğrulama Gerekli', value: 3 },
                      { id: '4', label: 'Şifre Gerekli', value: 4 },
                      { id: '5', label: 'Kilitli', value: 5 },
                      { id: '6', label: 'Askıya Alındı', value: 6 },
                      { id: '7', label: 'Devre Dışı', value: 7 },
                      { id: '8', label: 'Şifre Değiştirmeli', value: 8 }
                    ]}
                    value={[
                      { id: '1', label: 'Aktif', value: 1 },
                      { id: '2', label: 'Pasif', value: 2 },
                      { id: '3', label: 'Doğrulama Gerekli', value: 3 },
                      { id: '4', label: 'Şifre Gerekli', value: 4 },
                      { id: '5', label: 'Kilitli', value: 5 },
                      { id: '6', label: 'Askıya Alındı', value: 6 },
                      { id: '7', label: 'Devre Dışı', value: 7 },
                      { id: '8', label: 'Şifre Değiştirmeli', value: 8 }
                    ].find(status => status.value === formData.userStatus) || { id: '1', label: 'Aktif', value: 1 }}
                    onChange={(status) => {
                      if (status) {
                        handleInputChange('userStatus', status.value);
                      }
                    }}
                    placeholder="Durum seç..."
                    className="w-full"
                  />
                </div>

              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-8 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Kaydet
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolUserEditForm;
